import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

interface CartItem {
  qty: number;
  [key: string]: unknown;
}

interface CheckoutCartState {
  cart?: {
    items?: CartItem[];
  };
  [key: string]: unknown;
}

interface AppState {
  Cart?: {
    _qty?: number;
  };
  [key: string]: unknown;
}

const useCartStore = defineStore(
  'cart',
  () => {
    const qty = ref<number>(0);
    const isSyncingFromStorage = ref<boolean>(false);

    const cartQty = computed(() => qty.value);

    function setQty(quantity: number): void {
      if (quantity < 0) {
        return;
      }
      qty.value = quantity;
    }

    function calculateQtyFromItems(items: CartItem[]): number {
      if (!Array.isArray(items)) {
        return 0;
      }
      return items.reduce((acc, item) => {
        const itemQty = item.qty ?? 0;
        return acc + itemQty;
      }, 0);
    }

    function syncQtyToStorage(): void {
      if (isSyncingFromStorage.value) {
        return;
      }

      try {
        const localAppState = localStorage.getItem('app');
        if (localAppState) {
          const appState = JSON.parse(localAppState) as AppState;
          appState.Cart ??= {};
          // eslint-disable-next-line no-underscore-dangle
          appState.Cart._qty = qty.value;
          localStorage.setItem('app', JSON.stringify(appState));
        } else {
          const newAppState: AppState = { Cart: { _qty: qty.value } };
          localStorage.setItem('app', JSON.stringify(newAppState));
        }
      } catch {
        // Skip error
      }
    }

    function syncQtyFromStorage(): void {
      isSyncingFromStorage.value = true;
      try {
        const checkoutState = localStorage.getItem('checkout');
        if (checkoutState) {
          const checkout = JSON.parse(checkoutState) as CheckoutCartState;
          if (checkout.cart?.items && Array.isArray(checkout.cart.items)) {
            const calculatedQty = calculateQtyFromItems(checkout.cart.items);
            if (calculatedQty >= 0) {
              qty.value = calculatedQty;
              isSyncingFromStorage.value = false;
              return;
            }
          }
        }

        const localAppState = localStorage.getItem('app');
        if (localAppState) {
          const appState = JSON.parse(localAppState) as AppState;
          // eslint-disable-next-line no-underscore-dangle
          if (appState.Cart && typeof appState.Cart._qty === 'number') {
            // eslint-disable-next-line no-underscore-dangle
            qty.value = appState.Cart._qty;
            isSyncingFromStorage.value = false;
            return;
          }
        }

        const cartBadge = document.querySelector('.cartheader__cart-badge');
        if (cartBadge) {
          const text = cartBadge.textContent?.trim() ?? '0';
          const parsedQty = parseInt(text, 10);
          if (!Number.isNaN(parsedQty) && parsedQty >= 0) {
            qty.value = parsedQty;
          }
        }
      } catch {
        const cartBadge = document.querySelector('.cartheader__cart-badge');
        if (cartBadge) {
          const text = cartBadge.textContent?.trim() ?? '0';
          const parsedQty = parseInt(text, 10);
          if (!Number.isNaN(parsedQty) && parsedQty >= 0) {
            qty.value = parsedQty;
          }
        }
      } finally {
        isSyncingFromStorage.value = false;
      }
    }

    function setupStorageListener() {
      window.addEventListener('storage', event => {
        if (event.key === 'app' || event.key === 'checkout') {
          syncQtyFromStorage();
        }
      });

      let lastLocalAppState: string | null = null;
      let lastCheckoutState: string | null = null;

      const checkAppState = (): void => {
        try {
          const currentCheckoutState = localStorage.getItem('checkout');
          if (currentCheckoutState !== lastCheckoutState && currentCheckoutState) {
            lastCheckoutState = currentCheckoutState;
            const checkout = JSON.parse(currentCheckoutState) as CheckoutCartState;
            if (checkout.cart?.items) {
              syncQtyFromStorage();
            }
          }

          const currentLocalAppState = localStorage.getItem('app');

          if (currentLocalAppState !== lastLocalAppState && currentLocalAppState) {
            lastLocalAppState = currentLocalAppState;
            const appState = JSON.parse(currentLocalAppState) as AppState;
            if (appState.Cart) {
              syncQtyFromStorage();
            }
          }
        } catch {
          // Skip error
        }
      };

      const intervalId = setInterval(checkAppState, 500);

      window.addEventListener('focus', () => {
        syncQtyFromStorage();
      });

      return () => {
        clearInterval(intervalId);
      };
    }

    if (typeof window !== 'undefined') {
      setupStorageListener();

      watch(
        qty,
        () => {
          syncQtyToStorage();
        },
        { immediate: false }
      );
    }

    syncQtyFromStorage();

    return {
      qty,
      cartQty,
      setQty,
      syncQtyFromStorage,
      syncQtyToStorage,
    };
  },
  {
    persist: {
      storage: localStorage,
      key: 'cart',
      pick: ['qty'],
    },
  }
);

export default useCartStore;
